import React, { useMemo } from 'react'
import ButtonGlobal from '@shared/Button'
import { SubmitHandler, useFormContext, useFormState } from 'react-hook-form'

import { OrderSalaryCardType, OrderType } from 'store/types/salaryCard'
import Dialog from '@shared/Dialog'
import { useState } from 'react'
import { AsanSigning } from '@shared/AsanSigning'
import { useHistory } from 'react-router-dom'
import { ModalProvider } from '@shared/Modal'
import { Col, Image, Row, Space, Typography } from 'antd'
import { AlertIconIntoDetail, DownloadBtnIco } from '@modules/CustomIcons'
import { useTranslation } from 'react-i18next'
import { useFetchBranchListQuery } from 'store/reducers/configs/configs.api'
import { sumAndFormat } from '@shared/libs/sumAndFormat'
import ResumeModalContent from '@app/pages/Orders/businessCardOrder/entities/ResumeModalContent/ResumeModalContent'
import {
  MobilOperatorType,
  useUpdateBusinessCardOrderMutation,
  useUpdateBusinessCardOrderStatusMutation,
} from '@app/store/reducers/salaryCards/businessCardOrder.api'
import Icon from '@ant-design/icons'
import { FileDownload } from '../../../FileDownload'

export function FieldUpdateActionsRight() {
  const [open, setOpen] = useState(false)
  const [formData, setFormData] = useState({})
  const { handleSubmit, trigger, getValues } = useFormContext()
  const { isValid } = useFormState()
  const { t } = useTranslation()
  const navigate = useHistory()

  const { data: branchList = [] } = useFetchBranchListQuery(undefined, {
    refetchOnMountOrArgChange: true,
  })

  const getBranchLabel = (value: string) =>
    branchList.find((item) => item.value === value)?.label || ''

  const [
    fetchUpdateSalaryCardOrder,
    { isLoading, isSuccess, data: orderData, error, status },
  ] = useUpdateBusinessCardOrderMutation()

  const [
    updateOrderStatus,
    { status: upStatus, isSuccess: isUpSuccess, reset: resetUp },
  ] = useUpdateBusinessCardOrderStatusMutation()

  const onSubmit: SubmitHandler<OrderSalaryCardType> = (data) => {
    setOpen(true)
    fetchUpdateSalaryCardOrder(data)
  }

  const resumeCompanyInfo_1 = [
    {
      title: t('orders.salaryCardOrder.inputLabels.cardCurrency'),
      value: formData?.cardCurrency,
    },
    {
      title: t('businessCardOrders.labels.paySytsem'),
      value: formData?.cardSystem,
    },
    {
      title: t('businessCardOrders.labels.cardName'),
      value: (
        <div style={{ position: 'relative' }}>
          {' '}
          <span style={{ marginRight: 65 }}>{formData?.cardName}</span>{' '}
          <div
            style={{
              borderRadius: '4px',
              padding: '4px',
              backgroundColor: '#fff',
              border: '1px solid #D9E3EC',
              cursor: 'pointer',
              position: 'absolute',
              top: -9,
              right: 0,
            }}
          >
            <Image
              width={49}
              height={30}
              preview={false}
              src={getValues('cardImage')}
            />
          </div>
        </div>
      ),
    },
    {
      title: t('businessCardOrders.labels.cardDuration'),
      value: `${formData?.cardDuration} ${t('businessCardOrders.labels.year')}`,
    },
    {
      title: t('businessCardOrders.labels.issueFree'),
      value: `${Number(formData?.commission?.issueFee).toFixed(2)} AZN`,
    },
    {
      title: t('orders.salaryCardOrder.inputLabels.note'),
      value: formData?.note,
    },
  ]

  const resumeCompanyInfo_2 = [
    {
      title: t('businessCardOrders.labels.cardAccount'),
      value: formData?.cardAccount,
    },
    {
      title: t('businessCardOrders.labels.accountBranch'),
      value: getBranchLabel(formData?.accountBranch),
    },
    {
      title: t('orders.salaryCardOrder.inputLabels.BranchTransferred'),
      value: getBranchLabel(formData?.transferredBranch),
    },
    {
      title: t('orders.salaryCardOrder.tableTitles.receivingTransfer'),
      value: t(
        `orders.salaryCardOrder.constants.${formData?.orderDeliveryType}`
      ),
    },
    {
      title: t('orders.salaryCardOrder.tableTitles.orderTypes'),
      value: t(`orders.salaryCardOrder.constants.${formData?.orderType}`),
    },
    {
      title: t('businessCardOrders.labels.totalCommission'),
      value: `${Number(formData?.commission?.preparationFee).toFixed(2)} AZN`,
    },
  ]

  const resumeCompanyInfo_3 = [
    {
      title: t('orders.salaryCardOrder.inputLabels.fullName'),
      value: formData?.trustedPerson?.fullName,
    },
    {
      title: t('orders.salaryCardOrder.inputLabels.pin'),
      value: formData?.trustedPerson?.pin,
    },
    {
      title: t('orders.salaryCardOrder.tableTitles.phoneNumber'),
      value: formData?.trustedPerson?.phone,
    },
    {
      title: t('orders.salaryCardOrder.inputLabels.expiryDate'),
      value: formData?.trustedPerson?.expiryDate,
    },
    {
      title: t('businessCardOrders.labels.totalPay'),
      value: `${sumAndFormat(
        formData?.commission?.issueFee,
        formData?.commission?.preparationFee
      )} AZN`,
    },
  ]
  const resumeUserInfo_1 = [
    {
      title: t('businessCardOrders.labels.cardHolderFullName'),
      value: formData?.cardHolder?.cardHolderFullName,
    },
    {
      title: (
        <>
          {t('businessCardOrders.labels.phoneNumber')} (
          <span style={{ color: '#015CD1' }}>
            {MobilOperatorType[formData?.cardHolder?.mobileOperatorName]}
          </span>
          )
        </>
      ),
      value: formData?.cardHolder?.phoneNumber,
    },
  ]

  const resumeUserInfo_2 = [
    {
      title: t('businessCardOrders.labels.pin'),
      value: formData?.cardHolder?.pin,
    },
    {
      title: t('businessCardOrders.labels.serialTypeAndNo'),
      value:
        formData?.cardHolder?.serialType + ' ' + formData?.cardHolder?.serialNo,
    },
  ]

  const resumeUserInfo_3 = []

  if (getValues('frontFileId')) {
    resumeUserInfo_3.push({
      title: (
        <FileDownload fileId={getValues('frontFileId')} title={t('businessCardOrders.idcard.frontFileName')} />
      ),
      value: <Icon component={DownloadBtnIco} />,
    })
  }

  if (getValues('backFileId')) {
    resumeUserInfo_3.push({
      title: (
        <FileDownload
          fileId={getValues('backFileId')}
          title={t('businessCardOrders.idcard.backFileName')}
        />
      ),
      value: <Icon component={DownloadBtnIco} />,
    })
  }

  return (
    <>
      <ButtonGlobal
        customType="default"
        color="#015CD1"
        onClick={() => navigate.push('/orders/business-card')}
      >
        {t('orders.salaryCardOrder.buttonsLabels.close')}
      </ButtonGlobal>
      <ModalProvider
        title={t('orders.detailsOrder')}
        destroyOnClose
        width="calc(100vw - 30%)"
        modalContent={
          <ResumeModalContent
            title={t('businessCardOrders.labels.cardHolderInfoTitle')}
            tableData={[resumeUserInfo_1, resumeUserInfo_2, resumeUserInfo_3]}
            dataSource={[
              resumeCompanyInfo_1,
              resumeCompanyInfo_2,
              resumeCompanyInfo_3,
            ]}
          />
        }
        footer={({ closeModal }) => (
          <Row justify="space-between" align="middle">
            <Col span={6}>
              {formData?.orderType === OrderType.URGENT && (
                <Row
                  style={{
                    width: '350px',
                    padding: '12px',
                    borderRadius: '8px',
                    backgroundColor: '#F3F6FA',
                    fontSize: '10px',
                    fontWeight: 500,
                    display: 'flex',
                  }}
                >
                  <div style={{ marginRight: '8px' }}>
                    <AlertIconIntoDetail />
                  </div>

                  <Typography.Text>
                    {t('orders.salaryCardOrder.constants.commissionFee')}
                  </Typography.Text>
                </Row>
              )}
            </Col>
            <Col>
              <Space>
                <ButtonGlobal
                  customType="default"
                  color="#015CD1"
                  onClick={() => closeModal()}
                >
                  {t('orders.salaryCardOrder.buttonsLabels.close')}
                </ButtonGlobal>
                <ButtonGlobal
                  loading={isLoading}
                  customType="primary"
                  disabled={!isValid}
                  onClick={handleSubmit(onSubmit)}
                >
                  {t('orders.salaryCardOrder.buttonsLabels.update')}
                </ButtonGlobal>
                <AsanSigning
                  docType="SCO"
                  asanKey="asanSignWithUpdate"
                  handleSigning={() => {}}
                  checkSignCallback={() => isValid || isLoading}
                  status={upStatus}
                  message={
                    isUpSuccess
                      ? t('orders.salaryCardOrder.restrictionTexts.asanSuccess')
                      : undefined
                  }
                  onComplete={(data) => {
                    updateOrderStatus({
                      orderNo: orderData.orderNo,
                      ...data,
                    })
                  }}
                  afterCloseFn={() => {
                    resetUp()
                    closeModal()
                    navigate.push('/orders/business-card')
                  }}
                  beforeFromSigning={async () => {
                    const isValid = await trigger()

                    if (isValid) {
                      let requestData
                      await handleSubmit((data) => {
                        requestData = data
                      })()

                      return {
                        mutation: fetchUpdateSalaryCardOrder,
                        requestData,
                        fileNameKey: 'orderNo',
                        successMessage: `${t('orders.updateButAsanError')} `,
                      }
                    }
                  }}
                />
              </Space>

              <Dialog
                open={open}
                title={t('common.attention')}
                message={isSuccess ? t('orders.updateSuccess') : error?.data}
                modalType={status}
                afterClose={() => {
                  setOpen(false)
                  closeModal()
                  navigate.push('/orders/business-card')
                }}
              />
            </Col>
          </Row>
        )}
        bodyStyle={{
          maxHeight: 'calc(100vh - 200px)',
          overflowY: 'auto',
        }}
      >
        {({ openModal, setStatus }) => (
          <ButtonGlobal
            loading={isLoading}
            customType="primary"
            disabled={!isValid}
            onClick={() => {
              openModal('')
              handleSubmit((data) => setFormData(data))()
            }}
          >
            {t('newTransfer.nextButton')}
          </ButtonGlobal>
        )}
      </ModalProvider>
    </>
  )
}
